const nativeLink = ({
  key,
  label,
  icon,
  routeName,
  params,
  query,
  activeOn = [],
  policyRouteName,
}) => ({
  key,
  label,
  icon,
  routeName,
  params,
  query,
  activeOn,
  policyRouteName,
  nativeRoute: true,
});

const translatedLink = (t, key, labelKey, icon, routeName, options = {}) =>
  nativeLink({
    key,
    // Every caller below passes a literal SIDEBAR key; the helper keeps the
    // route inventory compact without turning labels into hard-coded copy.
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: t(labelKey),
    icon,
    routeName,
    ...options,
  });

const conversationChildren = ({
  t,
  inboxes,
  labels,
  teams,
  conversationCustomViews,
}) => [
  translatedLink(
    t,
    'native-conversations-mentions',
    'SIDEBAR.MENTIONED_CONVERSATIONS',
    'i-lucide-at-sign',
    'conversation_mentions',
    { activeOn: ['conversation_through_mentions'] }
  ),
  translatedLink(
    t,
    'native-conversations-unattended',
    'SIDEBAR.UNATTENDED_CONVERSATIONS',
    'i-lucide-circle-alert',
    'conversation_unattended',
    { activeOn: ['conversation_through_unattended'] }
  ),
  ...conversationCustomViews.map(view =>
    nativeLink({
      key: `native-conversation-folder-${view.id}`,
      label: `${t('SIDEBAR.CUSTOM_VIEWS_FOLDER')} · ${view.name}`,
      icon: 'i-lucide-folder',
      routeName: 'folder_conversations',
      params: { id: view.id },
      activeOn: ['conversations_through_folders'],
    })
  ),
  ...teams.map(team =>
    nativeLink({
      key: `native-conversation-team-${team.id}`,
      label: `${t('SIDEBAR.TEAMS')} · ${team.name}`,
      icon: 'i-lucide-users',
      routeName: 'team_conversations',
      params: { teamId: team.id },
      activeOn: ['conversations_through_team'],
    })
  ),
  ...inboxes.map(inbox =>
    nativeLink({
      key: `native-conversation-inbox-${inbox.id}`,
      label: `${t('SIDEBAR.CHANNELS')} · ${inbox.name}`,
      icon: 'i-lucide-mailbox',
      routeName: 'inbox_dashboard',
      params: { inbox_id: inbox.id },
      activeOn: ['conversation_through_inbox'],
    })
  ),
  ...labels.map(label =>
    nativeLink({
      key: `native-conversation-label-${label.id}`,
      label: `${t('SIDEBAR.LABELS')} · ${label.title}`,
      icon: 'i-lucide-tag',
      routeName: 'label_conversations',
      params: { label: label.title },
      activeOn: ['conversations_through_label'],
    })
  ),
];

const contactChildren = ({ t, labels, contactCustomViews }) => [
  translatedLink(
    t,
    'native-contacts-all',
    'SIDEBAR.ALL_CONTACTS',
    'i-lucide-contact',
    'contacts_dashboard_index',
    {
      query: { page: 1, search: undefined },
      activeOn: ['contacts_edit'],
    }
  ),
  translatedLink(
    t,
    'native-contacts-active',
    'SIDEBAR.ACTIVE',
    'i-lucide-user-check',
    'contacts_dashboard_active'
  ),
  ...contactCustomViews.map(view =>
    nativeLink({
      key: `native-contact-segment-${view.id}`,
      label: `${t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS')} · ${view.name}`,
      icon: 'i-lucide-group',
      routeName: 'contacts_dashboard_segments_index',
      params: { segmentId: view.id },
      query: { page: 1 },
      activeOn: ['contacts_edit_segment'],
    })
  ),
  ...labels.map(label =>
    nativeLink({
      key: `native-contact-label-${label.id}`,
      label: `${t('SIDEBAR.TAGGED_WITH')} · ${label.title}`,
      icon: 'i-lucide-tag',
      routeName: 'contacts_dashboard_labels_index',
      params: { label: label.title },
      query: { page: 1, search: undefined },
      activeOn: ['contacts_edit_label'],
    })
  ),
];

const reportChildren = t => [
  translatedLink(
    t,
    'native-reports-overview',
    'SIDEBAR.REPORTS_OVERVIEW',
    'i-lucide-chart-no-axes-combined',
    'account_overview_reports'
  ),
  translatedLink(
    t,
    'native-reports-conversations',
    'SIDEBAR.REPORTS_CONVERSATION',
    'i-lucide-message-square-text',
    'conversation_reports'
  ),
  translatedLink(
    t,
    'native-reports-agents',
    'SIDEBAR.REPORTS_AGENT',
    'i-lucide-user-round-search',
    'agent_reports_index',
    {
      policyRouteName: 'account_overview_reports',
      activeOn: ['agent_reports_show'],
    }
  ),
  translatedLink(
    t,
    'native-reports-labels',
    'SIDEBAR.REPORTS_LABEL',
    'i-lucide-tags',
    'label_reports_index',
    {
      policyRouteName: 'account_overview_reports',
      activeOn: ['label_reports_show'],
    }
  ),
  translatedLink(
    t,
    'native-reports-inboxes',
    'SIDEBAR.REPORTS_INBOX',
    'i-lucide-inbox',
    'inbox_reports_index',
    {
      policyRouteName: 'account_overview_reports',
      activeOn: ['inbox_reports_show'],
    }
  ),
  translatedLink(
    t,
    'native-reports-teams',
    'SIDEBAR.REPORTS_TEAM',
    'i-lucide-users-round',
    'team_reports_index',
    {
      policyRouteName: 'account_overview_reports',
      activeOn: ['team_reports_show'],
    }
  ),
  translatedLink(
    t,
    'native-reports-csat',
    'SIDEBAR.CSAT',
    'i-lucide-smile',
    'csat_reports'
  ),
  translatedLink(
    t,
    'native-reports-sla',
    'SIDEBAR.REPORTS_SLA',
    'i-lucide-clock-alert',
    'sla_reports'
  ),
  translatedLink(
    t,
    'native-reports-bot',
    'SIDEBAR.REPORTS_BOT',
    'i-lucide-bot',
    'bot_reports'
  ),
];

const campaignChildren = t => [
  translatedLink(
    t,
    'native-campaigns-live-chat',
    'SIDEBAR.LIVE_CHAT',
    'i-lucide-message-circle-more',
    'campaigns_livechat_index'
  ),
  translatedLink(
    t,
    'native-campaigns-sms',
    'SIDEBAR.SMS',
    'i-lucide-message-square',
    'campaigns_sms_index'
  ),
  translatedLink(
    t,
    'native-campaigns-whatsapp',
    'SIDEBAR.WHATSAPP',
    'i-lucide-message-circle',
    'campaigns_whatsapp_index'
  ),
  translatedLink(
    t,
    'native-campaigns-infojobs',
    'CAMPAIGN.INFOJOBS.NAV_LABEL',
    'i-lucide-briefcase-business',
    'campaigns_infojobs_index'
  ),
  translatedLink(
    t,
    'native-campaigns-email',
    'SIDEBAR.EMAIL',
    'i-lucide-mail',
    'campaigns_email_index'
  ),
  translatedLink(
    t,
    'native-campaigns-audience',
    'SIDEBAR.AUDIENCE',
    'i-lucide-users-round',
    'campaigns_audience_index'
  ),
];

export const hasHelpCenterPortal = portals =>
  Array.isArray(portals) && portals.some(portal => portal?.slug);

const helpCenterChildren = (t, portals = []) => {
  if (!hasHelpCenterPortal(portals)) {
    return [
      translatedLink(
        t,
        'native-help-center-create-portal',
        'HELP_CENTER.NEW_PAGE.CREATE_PORTAL_BUTTON',
        'i-lucide-plus',
        'portals_new',
        {
          activeOn: ['portals_index'],
        }
      ),
    ];
  }

  return [
    translatedLink(
      t,
      'native-help-center-articles',
      'SIDEBAR.HELP_CENTER.ARTICLES',
      'i-lucide-file-text',
      'portals_index',
      {
        params: { navigationPath: 'portals_articles_index' },
        activeOn: [
          'portals_articles_index',
          'portals_articles_new',
          'portals_articles_edit',
        ],
      }
    ),
    translatedLink(
      t,
      'native-help-center-categories',
      'SIDEBAR.HELP_CENTER.CATEGORIES',
      'i-lucide-folders',
      'portals_index',
      {
        params: { navigationPath: 'portals_categories_index' },
        activeOn: [
          'portals_categories_index',
          'portals_categories_articles_index',
          'portals_categories_articles_edit',
        ],
      }
    ),
    translatedLink(
      t,
      'native-help-center-locales',
      'SIDEBAR.HELP_CENTER.LOCALES',
      'i-lucide-languages',
      'portals_index',
      {
        params: { navigationPath: 'portals_locales_index' },
        activeOn: ['portals_locales_index'],
      }
    ),
    translatedLink(
      t,
      'native-help-center-settings',
      'SIDEBAR.HELP_CENTER.SETTINGS',
      'i-lucide-settings-2',
      'portals_index',
      {
        params: { navigationPath: 'portals_settings_index' },
        activeOn: ['portals_settings_index'],
      }
    ),
  ];
};

const settingsChildren = t => [
  translatedLink(
    t,
    'native-settings-account',
    'SIDEBAR.ACCOUNT_SETTINGS',
    'i-lucide-briefcase-business',
    'general_settings_index'
  ),
  translatedLink(
    t,
    'native-settings-teams',
    'SIDEBAR.TEAMS',
    'i-lucide-users',
    'settings_teams_list',
    {
      activeOn: [
        'settings_teams_new',
        'settings_teams_finish',
        'settings_teams_add_agents',
        'settings_teams_edit',
        'settings_teams_edit_members',
        'settings_teams_edit_finish',
      ],
    }
  ),
  translatedLink(
    t,
    'native-settings-assignment',
    'SIDEBAR.AGENT_ASSIGNMENT',
    'i-lucide-user-cog',
    'assignment_policy_index',
    {
      // The original Chatwoot sidebar exposes this entry only with the
      // advanced-assignment entitlement, even though its landing route uses
      // the broader assignment_v2 flag.
      policyRouteName: 'agent_capacity_policy_index',
      activeOn: [
        'agent_assignment_policy_index',
        'agent_assignment_policy_create',
        'agent_assignment_policy_edit',
        'agent_capacity_policy_index',
        'agent_capacity_policy_create',
        'agent_capacity_policy_edit',
      ],
    }
  ),
  translatedLink(
    t,
    'native-settings-inboxes',
    'SIDEBAR.INBOXES',
    'i-lucide-inbox',
    'settings_inbox_list',
    {
      activeOn: [
        'settings_inbox_show',
        'settings_inbox_new',
        'settings_inbox_finish',
        'settings_inboxes_page_channel',
        'settings_inboxes_add_agents',
      ],
    }
  ),
  translatedLink(
    t,
    'native-settings-labels',
    'SIDEBAR.LABELS',
    'i-lucide-tags',
    'labels_list'
  ),
  translatedLink(
    t,
    'native-settings-attributes',
    'SIDEBAR.CUSTOM_ATTRIBUTES',
    'i-lucide-braces',
    'attributes_list'
  ),
  translatedLink(
    t,
    'native-settings-automation',
    'SIDEBAR.AUTOMATION',
    'i-lucide-repeat-2',
    'automation_list',
    { activeOn: ['automation_approval_risk_matrix'] }
  ),
  translatedLink(
    t,
    'native-settings-surveys',
    'SIDEBAR.SURVEYS',
    'i-lucide-clipboard-list',
    'settings_surveys_index',
    {
      activeOn: [
        'settings_surveys_new',
        'settings_surveys_edit',
        'settings_surveys_responses',
      ],
    }
  ),
  translatedLink(
    t,
    'native-settings-macros',
    'SIDEBAR.MACROS',
    'i-lucide-toy-brick',
    'macros_wrapper',
    { activeOn: ['macros_new', 'macros_edit'] }
  ),
  translatedLink(
    t,
    'native-settings-canned-responses',
    'SIDEBAR.CANNED_RESPONSES',
    'i-lucide-message-square-quote',
    'canned_list'
  ),
  translatedLink(
    t,
    'native-settings-message-templates',
    'SIDEBAR.MESSAGE_TEMPLATES',
    'i-lucide-file-stack',
    'settings_message_templates'
  ),
  translatedLink(
    t,
    'native-settings-integrations',
    'SIDEBAR.INTEGRATIONS',
    'i-lucide-blocks',
    'settings_applications',
    {
      activeOn: [
        'settings_integrations_dashboard_apps',
        'settings_integrations_webhook',
        'settings_integrations_slack',
        'settings_integrations_linear',
        'settings_integrations_notion',
        'settings_integrations_shopify',
        'settings_applications_integration',
      ],
    }
  ),
  translatedLink(
    t,
    'native-settings-audit-logs',
    'SIDEBAR.AUDIT_LOGS',
    'i-lucide-file-clock',
    'auditlogs_list'
  ),
  translatedLink(
    t,
    'native-settings-custom-roles',
    'SIDEBAR.CUSTOM_ROLES',
    'i-lucide-shield-plus',
    'custom_roles_list'
  ),
  translatedLink(
    t,
    'native-settings-sla',
    'SIDEBAR.SLA',
    'i-lucide-clock-alert',
    'sla_list'
  ),
  translatedLink(
    t,
    'native-settings-workflow',
    'SIDEBAR.CONVERSATION_WORKFLOW',
    'i-lucide-workflow',
    'conversation_workflow_index'
  ),
  translatedLink(
    t,
    'native-settings-security',
    'SIDEBAR.SECURITY',
    'i-lucide-shield-check',
    'security_settings_index'
  ),
  translatedLink(
    t,
    'native-settings-billing',
    'SIDEBAR.BILLING',
    'i-lucide-credit-card',
    'billing_settings_index'
  ),
];

/**
 * Recreates every destination exposed by Chatwoot's native sidebar while
 * keeping the LAIS shell and visual hierarchy. Dynamic routes are materialized
 * from the same Vuex collections used by the original sidebar.
 */
export const buildNativeChatwootNavigation = ({
  t,
  inboxes = [],
  labels = [],
  teams = [],
  contactCustomViews = [],
  conversationCustomViews = [],
  portals = [],
}) => [
  {
    title: 'Chatwoot nativo',
    items: [
      translatedLink(
        t,
        'native-inbox-view',
        'SIDEBAR.INBOX',
        'i-lucide-inbox',
        'inbox_view',
        { activeOn: ['inbox_view_conversation'] }
      ),
      nativeLink({
        key: 'native-search',
        label: t('COMBOBOX.SEARCH_PLACEHOLDER'),
        icon: 'i-lucide-search',
        routeName: 'search',
      }),
      ...conversationChildren({
        t,
        inboxes,
        labels,
        teams,
        conversationCustomViews,
      }),
      ...contactChildren({ t, labels, contactCustomViews }),
      translatedLink(
        t,
        'native-companies',
        'SIDEBAR.COMPANIES',
        'i-lucide-building-2',
        'companies_dashboard_index'
      ),
    ],
  },
  {
    title: 'Relatórios nativos',
    items: reportChildren(t),
  },
  {
    title: 'Campanhas nativas',
    items: campaignChildren(t),
  },
  {
    title: 'Central de ajuda nativa',
    items: helpCenterChildren(t, portals),
  },
  {
    title: 'Configurações nativas',
    items: settingsChildren(t),
  },
];

export const REQUIRED_NATIVE_CHATWOOT_ROUTE_NAMES = [
  'inbox_view',
  'search',
  'conversation_mentions',
  'conversation_unattended',
  'folder_conversations',
  'team_conversations',
  'inbox_dashboard',
  'label_conversations',
  'contacts_dashboard_index',
  'contacts_dashboard_active',
  'contacts_dashboard_segments_index',
  'contacts_dashboard_labels_index',
  'companies_dashboard_index',
  'account_overview_reports',
  'conversation_reports',
  'agent_reports_index',
  'label_reports_index',
  'inbox_reports_index',
  'team_reports_index',
  'csat_reports',
  'sla_reports',
  'bot_reports',
  'campaigns_livechat_index',
  'campaigns_sms_index',
  'campaigns_whatsapp_index',
  'campaigns_infojobs_index',
  'campaigns_email_index',
  'campaigns_audience_index',
  'portals_index',
  'general_settings_index',
  'settings_teams_list',
  'assignment_policy_index',
  'settings_inbox_list',
  'labels_list',
  'attributes_list',
  'automation_list',
  'settings_surveys_index',
  'macros_wrapper',
  'canned_list',
  'settings_message_templates',
  'settings_applications',
  'auditlogs_list',
  'custom_roles_list',
  'sla_list',
  'conversation_workflow_index',
  'security_settings_index',
  'billing_settings_index',
];
